import {
  getProduct as amalgamGetProduct,
  getAllColorOptions as amalgamGetAllColorOptions,
  getAllSizeOptions as amalgamGetAllSizeOptions,
  mergeNewVariantWithAmalgam,
  updatePicturesDimension as updateDimensionInAmalgam,
  getCompanyProductType as companyProductType,
  getCurrencyOptions,
  getCompanyUnitDetail
} from './Amalgam';
import { getMaximumItemCount, shouldShowProduct } from './Inventory';
import {
  redirectToErrorPage,
  HOME_LAYOUT_TYPE,
  isResellerBundleView,
  RETURN_URI,
  encodeReturnUriForSearch,
  MEDIA_TYPES,
  getPictures,
  getImageWidth,
  getReturnUrl,
  isStringValidForSeoTag,
  logException,
  isSearchResultView
} from './Utils';
import { getSingleVariantMeta as apiGetSingleVariantMeta, updateDimension } from './Api';
import { route } from 'preact-router';
import { getShowcaseSlug } from './Showcase';
import { setProductCache, getCachedProduct } from './ProductCache';
import { getCompanyId } from './Company';
import I18n from './I18n';
import { getRouteParams } from './Route';
import { getFormattedPrice } from './locale';
import { getObjectValues } from '../../../utils/GlobalUtils';
import { downloadFromUrl } from './Picture';
import { getCachedVariantsDefaultImages } from './Variants';
import { getJewelleryTypeProductsCharges } from './JewelleryTypeProduct';

let contentBounced = false;
let imageViewImageUrl = null;
let shouldScrollToOptions = false;

export const defaultImageHeightMagazineView = 500;
export const defaultImageHeightListView = 0;

export const OPTION_TYPE = {
  UNAVAILABLE_VARIANT_REASONS: {
    INVALID_INVENTORY: {
      id: 'INVALID_INVENTORY',
      reason: (singleOptionType) =>
        singleOptionType ? I18n.t('OUT_OF_STOCK') : I18n.t('OUT_OF_STOCK_IN')
    },
    DELETED: {
      id: 'DELETED',
      reason: (singleOptionType) =>
        singleOptionType ? I18n.t('NOT_AVAILABLE') : I18n.t('NOT_AVAILABLE_IN')
    }
  },
  COLORS: {
    id: 'COLORS',
    noOptionSelectedText: () => I18n.t('SELECT_COLORS'),
    optionPickerModalTitle: () => I18n.t('SELECT_COLORS')
  },
  SIZES: {
    id: 'SIZES',
    noOptionSelectedText: () => I18n.t('SELECT_SIZES'),
    optionPickerModalTitle: () => I18n.t('SELECT_SIZES')
  },
  INVENTORY: {
    id: 'INVENTORY',
    noOptionSelectedText: () => I18n.t('SELECT_QUANTITY'),
    optionPickerModalTitle: () => I18n.t('SELECT_QUANTITY')
  }
};

const processProduct = (product) => {
  if (!product) {
    return null;
  }

  product.getPriceString = () => {
    if (
      product.isSlab &&
      ((product.slabPrices || {}).prices || []).length > 0 &&
      product.slabPrices.prices[0].price !== product.price
    ) {

      return getFormattedPrice(product.slabPrices.prices[0].price);
    }

    return product.formattedDiscountedPrice || product.formattedPrice || '';
  };

  product.getCutPriceString = () => {
    if (
      product.isSlab &&
      ((product.slabPrices || {}).prices || []).length > 0 &&
      product.slabPrices.prices[0].price !== product.price
    ) {
      return getFormattedPrice(product.price);
    }
    return product.formattedDiscountedPrice && product.formattedPrice ? product.formattedPrice : '';
  };

  product.getTaxString = () => {
    if (product.taxes && product.taxes.length) {
      return `${product.taxes[0].taxPercentage}% ${product.taxes[0].taxType}`;
    }

    return '';
  };

  product.getTaxPrice = () => (product.taxedPrice ? product.taxedPrice - product.price : null);

  product.getTaxedPriceString = (margin) => {
    if (
      product.isSlab &&
      product.slabPrices &&
      product.slabPrices.prices &&
      product.slabPrices.prices.length &&
      companyProductType() !== 'JEWELLERY'
    ) {
      const { taxPercentage } = (product.taxes || [])[0] || {};
      if (!taxPercentage) return '';
      const firstPrice = product.slabPrices.prices[0].price;
      const resellingSlabPrice = firstPrice + (margin || 0);
      return getFormattedPrice(
        resellingSlabPrice + (resellingSlabPrice * Number(taxPercentage)) / 100
      );
    }

    if (!margin && companyProductType() !== 'JEWELLERY') {
      return product.formattedTaxedPrice || '';
    }
    const { taxPercentage } = (product.taxes || [])[0] || {};
    if (!taxPercentage) return '';

    if (!margin && companyProductType() === 'JEWELLERY') {
      return getFormattedPrice(product.price + (product.price * Number(taxPercentage)) / 100);
    }

    let resellingPrice = 0;

    companyProductType() !== 'JEWELLERY'
      ? (resellingPrice = (product.discounted_price || product.price) + margin)
      : (resellingPrice = product.price + margin);
    return getFormattedPrice(resellingPrice + (resellingPrice * Number(taxPercentage)) / 100);
  };

  product.getSavedAndDiscountPriceString = () => {
    const { price, discounted_price: discountedPrice } = product;
    if (!price || !discountedPrice) {
      return null;
    }

    const amountSaved = price - discountedPrice;

    const discountPercent = (Math.abs(price - discountedPrice) / price) * 100;

    const getDiscountPercentage = I18n.t('DISCOUNT_PERCENT', {
      discount: Math.round(discountPercent)
    });

    return `${getFormattedPrice(amountSaved)} ${
      getDiscountPercentage ? `(${getDiscountPercentage})` : ''
    }`;
  };

  product.isVariant = () => !!product.parentProductId;

  product.getJewelleryTypeCharges = () => getJewelleryTypeProductsCharges(product);

  if (!product.sku && product.parentProductId) {
    const parentProduct = getProduct(product.parentProductId);
    if (parentProduct && parentProduct.sku) {
      product.sku = parentProduct.sku;
    }
  }

  if (!product.setType && product.parentProductId) {
    const parentProduct = getProduct(product.parentProductId);
    if (parentProduct && parentProduct.setType) {
      product.setType = parentProduct.setType;
    }
  }

  if (!product.pictures) {
    product.pictures = {};
  }

  const key = product.id;
  const value = product;

  setProductCache(key, value);

  return product;
};

export const getProduct = (productId, fromCustomPage) => {
  const cachedProduct = getCachedProduct(productId);
  if (!!cachedProduct && !!cachedProduct.id && !fromCustomPage && !isSearchResultView()) {
    return cachedProduct;
  }
  const unprocessedProduct = amalgamGetProduct(productId, fromCustomPage);
  const processedProduct = processProduct(unprocessedProduct);
  return processedProduct;
};

export const getImageHeight = ({
  product,
  mediaId,
  mediaType,
  descriptionRuntimeHeight,
  productNameHeight,
  onHeightCalculated,
  layoutType
}) => {
  const width = getImageWidth(layoutType);

  if (product !== null) {
    const pictures = product.pictures || {};
    const pictureData = pictures[mediaId];

    const videos = product.videos || {};
    const videoData = videos[mediaId];

    const productId = product.id;
    if (pictureData && pictureData.width && pictureData.height) {
      const height = Math.round(width * (pictureData.height / pictureData.width));
      onHeightCalculated({
        width,
        height,
        product,
        mediaId,
        descriptionRuntimeHeight,
        productNameHeight
      });
    } else if (
      mediaType === MEDIA_TYPES.VIDEO &&
      videoData &&
      videoData.width &&
      videoData.height
    ) {
      const height = Math.round(width * (videoData.height / videoData.width));
      onHeightCalculated({
        width,
        height,
        product,
        mediaId,
        descriptionRuntimeHeight,
        productNameHeight
      });
    } else {
      let height =
        layoutType === HOME_LAYOUT_TYPE.LIST
          ? defaultImageHeightListView
          : defaultImageHeightMagazineView;
      onHeightCalculated({
        width,
        height,
        product,
        mediaId,
        descriptionRuntimeHeight,
        productNameHeight
      });

      if (mediaType !== MEDIA_TYPES.VIDEO) {
        const img = new Image();
        img.onload = function () {
          height = Math.round(width * (this.naturalHeight / this.naturalWidth));
          onHeightCalculated({ width, height, descriptionRuntimeHeight, productNameHeight });
          updatePictureDimensions({ productId, pictureId: mediaId, width, height });
          updateDimensionInAmalgam({ productId, pictureId: mediaId, width, height }); // update the picture dimension in amalgamObject
          img.onerror = function () {};
          img.src = '';
        };
        img.onerror = function () {
          img.onerror = function () {};
          img.src = '';
        };
        img.src = pictures[mediaId] ? pictures[mediaId].url : getProductPictureUrl(product);
      }
    }
  }
};

const updatePictureDimensions = ({ productId, pictureId, width, height }) => {
  updateDimension({ productId, pictureId, width, height })
    .then(() => Promise.resolve())
    .catch((err) => Promise.reject(err));
};

export const contentDidBounce = () => {
  contentBounced = true;
};

export const didContentBounce = () => contentBounced;

export const isHorizontalImage = (picture) => {
  if (!picture || !picture.width || !picture.height) {
    return false;
  }

  return picture.width / picture.height > 3;
};

export const getProductPictureUrl = (product) => {
  if (!product) {
    return null;
  }
  if (
    product.default_picture_id &&
    product.pictures &&
    product.pictures[product.default_picture_id] &&
    product.pictures[product.default_picture_id].url
  ) {
    return product.pictures[product.default_picture_id].url;
  }

  if (product.pictureUrl) {
    return product.pictureUrl;
  }

  if (
    product.default_picture_id &&
    product.pictures &&
    Object.keys(product.pictures).length &&
    !product.pictures[product.default_picture_id]
  ) {
    const pictureIds = Object.keys(product.pictures);

    for (let i = 0; i < pictureIds.length; ++i) {
      if (product.pictures[pictureIds[i]] && product.pictures[pictureIds[i]].url) {
        return product.pictures[pictureIds[i]].url;
      }
    }
  }
  const picturesList = product.pictures ? getObjectValues(product.pictures) : [];
  if (product.pictures && picturesList.length > 0) {
    return picturesList[0].url;
  }

  return null;
};

export const getProductPictureId = (product) => {
  if (!product) {
    return null;
  }
  if (
    product.default_picture_id &&
    product.pictures &&
    product.pictures[product.default_picture_id] &&
    (product.pictures[product.default_picture_id].id ||
      product.pictures[product.default_picture_id].pictureId)
  ) {
    return (
      product.pictures[product.default_picture_id].id ||
      product.pictures[product.default_picture_id].pictureId
    );
  }

  if (product.pictureUrl && product.pictures) {
    const pictures = getObjectValues(product.pictures);
    let pictureId = null;
    for (let m = 0; m < pictures.length; m++) {
      if (pictures[m].url === product.pictureUrl) {
        pictureId = pictures[m].id || pictures[m].pictureId;
        break;
      }
    }

    if (pictureId) {
      return pictureId;
    }
  }

  if (
    product.default_picture_id &&
    product.pictures &&
    Object.keys(product.pictures).length > 0 &&
    !product.pictures[product.default_picture_id]
  ) {
    const pictureIds = Object.keys(product.pictures);

    for (let i = 0; i < pictureIds.length; ++i) {
      if (
        product.pictures[pictureIds[i]] &&
        (product.pictures[pictureIds[i]].id || product.pictures[pictureIds[i]].pictureId)
      ) {
        return product.pictures[pictureIds[i]].id || product.pictures[pictureIds[i]].pictureId;
      }
    }
  }

  const picturesList = product.pictures ? getObjectValues(product.pictures) : [];
  if (product.pictures && picturesList.length > 0) {
    return picturesList[0].id || picturesList[0].pictureId;
  }

  return null;
};

export const getProductPictureUrlFromPictureId = (product, pictureId) => {
  if (!product) {
    return null;
  }
  if (product.pictures && product.pictures[pictureId] && product.pictures[pictureId].url) {
    return product.pictures[pictureId].url;
  }

  if (product.pictureUrl) {
    return product.pictureUrl;
  }

  return null;
};

export const getProductDefaultPictureId = (product) => {
  if (!product) {
    return null;
  }
  if (
    product.default_picture_id &&
    product.pictures &&
    product.pictures[product.default_picture_id] &&
    product.pictures[product.default_picture_id].url
  ) {
    return product.default_picture_id;
  }

  if (product.pictures && Object.keys(product.pictures).length) {
    const pictureIds = Object.keys(product.pictures);

    for (let i = 0; i < pictureIds.length; ++i) {
      if (product.pictures[pictureIds[i]] && product.pictures[pictureIds[i]].url) {
        return pictureIds[i];
      }
    }
  }

  return null;
};

export const downloadProductPicture = (productId, pictureId) => {
  const product = getProduct(productId);

  let url;
  if (
    product &&
    product.pictures &&
    product.pictures[pictureId] &&
    product.pictures[pictureId].url
  ) {
    url = product.pictures[pictureId].url;
  } else {
    url = getProductPictureUrl(product);
  }

  if (!url) {
    return Promise.resolve();
  }

  return downloadFromUrl(url);
};

export const getPictureDominantColor = (picture) => {
  const paletteType = 'Vibrant';

  if (!picture || !picture.id || !picture.url) {
    return '#ffffff';
  }

  if (picture.colorPalette && picture.colorPalette[paletteType]) {
    return picture.colorPalette[paletteType];
  }

  return '#ffffff';
};

export const getProductDominantColor = (product) => {
  const paletteType = 'Vibrant';

  if (!product) {
    return '#ffffff';
  }

  if (
    product.default_picture_id &&
    product.pictures &&
    product.pictures[product.default_picture_id] &&
    product.pictures[product.default_picture_id].colorPalette &&
    product.pictures[product.default_picture_id].colorPalette[paletteType]
  ) {
    return product.pictures[product.default_picture_id].colorPalette[paletteType];
  }

  if (Object.keys(product.pictures).length) {
    const pictureIds = Object.keys(product.pictures);

    for (let i = 0; i < pictureIds.length; ++i) {
      if (
        product.pictures[pictureIds[i]] &&
        product.pictures[pictureIds[i]].colorPalette &&
        product.pictures[pictureIds[i]].colorPalette[paletteType]
      ) {
        return product.pictures[pictureIds[i]].colorPalette[paletteType];
      }
    }
  }

  return '#ffffff';
};

export const setImageViewImageUrl = (imageUrl) => {
  imageViewImageUrl = imageUrl;
};

export const getImageViewImageUrl = () => imageViewImageUrl;

export const setShouldScrollToOptions = (value) => {
  shouldScrollToOptions = value;
};

export const getShouldScrollToOptions = () => shouldScrollToOptions;

export const scrollOptionIntoView = () => {
  const shouldScroll = getShouldScrollToOptions();
  const element = document.getElementById('ProductVariantsContainer');
  if (element && typeof element.getBoundingClientRect === 'function') {
    const { top } = element.getBoundingClientRect();
    if (shouldScroll && top) {
      setTimeout(() => {
        window.scrollTo(0, top);
        setShouldScrollToOptions(false);
      });
    }
  }
};

export const getAllOptions = ({ parentProductId, productId, optionType, fromCustomPage }, topN) => {
  let id = parentProductId;
  if (!id) {
    const product = amalgamGetProduct(productId, fromCustomPage);
    if (product) {
      id = product.parentProductId;
    }
  }

  if (!id || !optionType) {
    // TODO: throw error
    return null;
  }

  if (optionType === OPTION_TYPE.COLORS.id) {
    const colorOptions = amalgamGetAllColorOptions(id, fromCustomPage);
    return colorOptions ? (topN ? colorOptions.slice(0, topN) : colorOptions) : null;
  } else if (optionType === OPTION_TYPE.SIZES.id) {
    const sizeOptions = amalgamGetAllSizeOptions(id, fromCustomPage);
    return sizeOptions ? (topN ? sizeOptions.slice(0, topN) : sizeOptions) : null;
  }

  return null;
};
export const checkCustomVariantExists = (parentProductId) => {
  if (!parentProductId) {
    return false;
  }
  const product = getProduct(parentProductId);
  const { allOptions = {} } = product;

  if (Object.keys(allOptions).length) {
    const copiedAllOptions = { ...allOptions };
    delete copiedAllOptions[OPTION_TYPE.COLORS.id];
    delete copiedAllOptions[OPTION_TYPE.SIZES.id];
    if (Object.keys(copiedAllOptions).length) {
      return true;
    }
  }
  return false;
};
export const getAllColorOptions = ({ parentProductId, productId }, topN) => {
  let id = parentProductId;

  if (!id) {
    id = amalgamGetProduct(productId).parentProductId;
  }

  if (!id) {
    // throw error
    return null;
  }

  const colorOptions = amalgamGetAllColorOptions(id);

  return colorOptions ? (topN ? colorOptions.slice(0, topN) : colorOptions) : null;
};

export const hasColorOptions = (productId) => {
  const product = getProduct(productId);

  if (!product || !product.isVariant() || !product.optionsMeta) {
    return false;
  }

  return !!product.optionsMeta.COLORS;
};

export const hasSizeOptions = (productId) => {
  const product = getProduct(productId);

  if (!product || !product.isVariant() || !product.optionsMeta) {
    return false;
  }

  return !!product.optionsMeta.SIZES;
};

export const getVariants = (productId) => {
  const product = getProduct(productId) || {};

  const { parentProductId, isVariant } = product;
  const parentProduct = getProduct(parentProductId);
  if (
    !parentProductId ||
    !productId ||
    typeof isVariant !== 'function' ||
    !isVariant() ||
    !parentProduct
  ) {
    return null;
  }

  const { allOptions } = parentProduct;

  const copiedVariants = { ...(allOptions || {}) };

  const variants = Object.keys(copiedVariants).map((option) => ({
    [option]: copiedVariants[option]
  }));

  return variants.length ? variants : null;
};

export const getVariantOptions = ({ productId, optionType }) => {
  if (!productId) {
    return null;
  }

  const product = getProduct(productId);

  if (!product || !product.optionsMeta) {
    return null;
  }

  if (optionType === OPTION_TYPE.COLORS.id && product.optionsMeta.COLORS) {
    return product.optionsMeta.COLORS;
  } else if (optionType === OPTION_TYPE.SIZES.id && product.optionsMeta.SIZES) {
    return product.optionsMeta.SIZES;
  }

  return null;
};

export const getCustomVariantOptionMeta = ({ productId, customVariantHeading }) => {
  if (!productId) {
    return null;
  }

  const product = getProduct(productId);

  if (!product || !product.optionsMeta) {
    return null;
  }
  const optionmeta = product.optionsMeta;
  return optionmeta[customVariantHeading];
};

export const getOptionAvailability = ({ productId, optionType }, { checkOOSConfig } = {}) => {
  const optionIdsAvailability = {};

  if (!productId) {
    logException('ProductId is not defined in getOptionAvailability', { productId, optionType });
    redirectToErrorPage();
    return;
  }

  const product = getProduct(productId) || {};

  if (!product || !product.isVariant()) {
    logException('Product is not a variant in getOptionAvailability', { productId, optionType });
    redirectToErrorPage();
    return;
  }
  const { optionsMeta, parentProductId } = product || {};

  const filteredOptions = {};
  Object.keys(optionsMeta)
    .filter((type) => type !== optionType)
    .forEach((optionType) => {
      filteredOptions[optionType] = optionsMeta[optionType];
    });

  const parentProduct = getProduct(parentProductId);

  const { availableVariants, unavailableVariants } = parentProduct;

  const isValidVariant = (meta) => {
    let unavailableIn = '';
    let isValid = true;

    getObjectValues(meta).forEach(({ optionValue, optionType: type, optionId }) => {
      if (optionType !== type) {
        unavailableIn += `${optionValue} (${type.toLowerCase()}) `;
        if (filteredOptions[type].optionId !== optionId) {
          isValid = false;
        }
      }
    });

    return {
      unavailableIn,
      isValid
    };
  };

  availableVariants.forEach((id) => {
    const itemCount = getMaximumItemCount(id);
    let productOOS = true;
    if (checkOOSConfig) {
      productOOS = !shouldShowProduct(id);
    }

    if (!itemCount && productOOS) {
      const { optionsMeta: meta = {} } = getProduct(id);
      const { unavailableIn, isValid } = isValidVariant(meta);

      if (!isValid) {
        return;
      }
      const { optionId, optionValue } = meta[optionType] || {};

      const reason = `${OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.INVALID_INVENTORY.reason(
        !unavailableIn
      )} ${unavailableIn}`;

      optionIdsAvailability[optionId] = {
        optionId,
        optionValue,
        unavailableDueTo: OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.INVALID_INVENTORY.id,
        reason
      };
    }
  });

  unavailableVariants.forEach((productId) => {
    const product = getProduct(productId);

    const { optionsMeta } = product;
    const { unavailableIn, isValid } = isValidVariant(optionsMeta);

    if (!isValid) {
      return;
    }

    const { optionId, optionValue } = optionsMeta[optionType] || {};
    const reason = `${OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.DELETED.reason(
      !unavailableIn
    )} ${unavailableIn}`;

    optionIdsAvailability[optionId] = {
      optionId,
      optionValue,
      unavailableDueTo: OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.DELETED.id,
      reason
    };
  });

  return optionIdsAvailability;
};

export const isProductVariant = ({ productId }) => getProduct(productId).isVariant();

export const findAvailableBulkOrderSizeCheckboxOptions = ({
  parentProductId,
  selectedColorOptionIds = []
}) => {
  const parentProduct = getProduct(parentProductId);
  const availableBulkOrderSizeOptions = [];
  if (!parentProduct || !parentProduct.allOptions) {
    return availableBulkOrderSizeOptions;
  }
  const allSizeOptions = parentProduct.allOptions[OPTION_TYPE.SIZES.id];
  const { availableVariants: availableVariantIds } = parentProduct;
  const availableVariants = availableVariantIds.map((variantId) => getProduct(variantId));
  allSizeOptions.forEach((sizeOption, index) => {
    // check if this size is available in atleast one selected color options
    const selectedColorVariantIndex = availableVariants.findIndex(
      (availableVariant) =>
        availableVariant.optionsMeta[OPTION_TYPE.SIZES.id].optionId === sizeOption.optionId &&
        selectedColorOptionIds.includes(
          availableVariant.optionsMeta[OPTION_TYPE.COLORS.id].optionId
        )
    );
    if (selectedColorVariantIndex > -1) {
      availableBulkOrderSizeOptions.push({
        id: sizeOption.optionId,
        optionId: sizeOption.optionId,
        value: sizeOption.optionValue,
        position: index
      });
    }
  });
  return availableBulkOrderSizeOptions;
};

export const findBulkOrderSizeVariantsForColorOption = ({ parentProductId, colorOptionId }) => {
  const variants = [];
  const parentProduct = getProduct(parentProductId);
  if (parentProduct && parentProduct.availableVariants) {
    parentProduct.availableVariants.forEach((variantId) => {
      const variant = getProduct(variantId);
      if (
        variant &&
        variant.optionsMeta &&
        variant.optionsMeta[OPTION_TYPE.SIZES.id] &&
        variant.optionsMeta[OPTION_TYPE.COLORS.id] &&
        variant.optionsMeta[OPTION_TYPE.COLORS.id].optionId === colorOptionId
      ) {
        variants.push({
          variantId: variant.id,
          price:
            variant.getPriceString() ||
            (parentProduct ? parentProduct.getPriceString() : undefined),
          isOutOfStock: getMaximumItemCount(variant.id) <= 0,
          sizeOptionId: variant.optionsMeta[OPTION_TYPE.SIZES.id].optionId,
          optionLabel: variant.optionsMeta[OPTION_TYPE.SIZES.id].optionValue,
          sizeOptionPosition: variant.optionsMeta[OPTION_TYPE.SIZES.id].position
        });
      }
    });
  }
  return variants.sort((a, b) => a.sizeOptionPosition - b.sizeOptionPosition);
};

export const findBulkOrderColorVariantsForSizeOption = ({ parentProductId, sizeOptionId }) => {
  const variants = [];
  const parentProduct = getProduct(parentProductId);
  if (parentProduct && parentProduct.availableVariants) {
    parentProduct.availableVariants.forEach((variantId) => {
      const variant = getProduct(variantId);
      if (
        variant &&
        variant.optionsMeta &&
        variant.optionsMeta[OPTION_TYPE.COLORS.id] &&
        variant.optionsMeta[OPTION_TYPE.SIZES.id] &&
        variant.optionsMeta[OPTION_TYPE.SIZES.id].optionId === sizeOptionId
      ) {
        variants.push({
          variantId: variant.id,
          price:
            variant.getPriceString() ||
            (parentProduct ? parentProduct.getPriceString() : undefined),
          isOutOfStock: getMaximumItemCount(variant.id) <= 0,
          colorOptionId: variant.optionsMeta[OPTION_TYPE.COLORS.id].optionId,
          colorOptionLabel: variant.optionsMeta[OPTION_TYPE.COLORS.id].optionValue,
          colorOptionPosition: variant.optionsMeta[OPTION_TYPE.COLORS.id].position
        });
      }
    });
  }
  return variants.sort((a, b) => a.sizeOptionPosition - b.sizeOptionPosition);
};

export const getFirstBulkOrderColorOptionVariantBackgroundImage = ({
  parentProductId,
  colorOptionId
}) => {
  const colorVariants = findBulkOrderSizeVariantsForColorOption({
    parentProductId,
    colorOptionId
  });
  const firstVariantWithSelectedColor = colorVariants[0];
  const cachedVariantsDefaultImages = getCachedVariantsDefaultImages({ parentProductId });
  if (cachedVariantsDefaultImages) {
    const cachedVariantsDefaultImage = cachedVariantsDefaultImages.find(
      (cachedVariantsDefaultImage) =>
        cachedVariantsDefaultImage.variantId === firstVariantWithSelectedColor.variantId
    );
    if (cachedVariantsDefaultImage) {
      return cachedVariantsDefaultImage.thumbnail;
    }
  }
  return firstVariantWithSelectedColor
    ? getProduct(firstVariantWithSelectedColor.variantId).pictureUrl
    : null;
};

export const findAvailableVariantFromOptions = (options, parentProductId) => {
  const { availableVariants } = getProduct(parentProductId);
  let variantId = null;

  for (let j = 0; j < availableVariants.length; j += 1) {
    const id = availableVariants[j];

    const product = getProduct(id);
    const optionsMeta = getObjectValues((product || {}).optionsMeta || {});
    let isValid = true;

    for (let i = 0; i < optionsMeta.length; i += 1) {
      const { optionId } = optionsMeta[i];

      if (options.indexOf(optionId) < 0) {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      variantId = id;
      break;
    }
  }

  return variantId;
};

const findNextAvailableVariant = (availableVariants, optionType, selectedOptionId) => {
  let newVariantId = null;

  for (let i = 0; i < availableVariants.length; i += 1) {
    const variantId = availableVariants[i];
    const product = getProduct(variantId);
    const { optionsMeta } = product;

    if (optionsMeta[optionType] && optionsMeta[optionType].optionId === selectedOptionId) {
      newVariantId = variantId;
      break;
    }
  }

  return newVariantId;
};

const handleDeletedVariantOptionsForNewSelection = ({
  availableVariants,
  optionType,
  newOptionId,
  allOptions
}) => {
  let newVariantId = findNextAvailableVariant(availableVariants, optionType, newOptionId);

  if (!newVariantId) {
    const options = allOptions[optionType];

    for (let i = 0; i < options.length; i += 1) {
      const { optionId } = options[i];

      if (optionId !== newOptionId) {
        newVariantId = findNextAvailableVariant(availableVariants, optionType, optionId);
      }

      if (newVariantId) {
        break;
      }
    }
  }

  return newVariantId;
};

export const selectNewOption = (
  { newOptionId, optionType, productId, unavailableDueTo },
  { beforeRedirection, fromBundle } = {}
) => {
  try {
    const product = getProduct(productId);
    if (!product) {
      const errorMessage = `Could not find product id: ${productId}`;
      logException(errorMessage, {
        newOptionId,
        optionType,
        productId,
        unavailableDueTo
      });
      redirectToErrorPage(errorMessage);
      return Promise.resolve();
    }

    const companyId = getCompanyId();
    const { parentProductId, optionsMeta } = product;

    const parentProduct = getProduct(parentProductId);
    const { availableVariants, allOptions } = parentProduct;

    const newOptions = {
      [optionType]: newOptionId
    };

    let newVariantId = null;

    if (
      !!unavailableDueTo &&
      unavailableDueTo === OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.INVALID_INVENTORY.id
    ) {
      const options = getObjectValues(optionsMeta)
        .filter(({ optionType: type }) => type !== optionType)
        .map(({ optionId }) => optionId);
      options.push(newOptionId);

      newVariantId = findAvailableVariantFromOptions(options, parentProductId);
    } else if (
      !!unavailableDueTo &&
      unavailableDueTo === OPTION_TYPE.UNAVAILABLE_VARIANT_REASONS.DELETED.id
    ) {
      newVariantId = handleDeletedVariantOptionsForNewSelection({
        availableVariants,
        optionType,
        newOptionId,
        allOptions
      });
    } else {
      Object.keys(product.optionsMeta)
        .filter((type) => type !== optionType)
        .forEach((optionType) => {
          newOptions[optionType] = product.optionsMeta[optionType].optionId;
        });

      for (let i = 0; i < availableVariants.length; i++) {
        let isSame = false;

        const variantId = availableVariants[i];
        const product = getProduct(variantId);
        const { optionsMeta } = product;

        const optionTypes = Object.keys(optionsMeta);

        for (let j = 0; j < optionTypes.length; j++) {
          const optionType = optionTypes[j];
          const { optionId } = optionsMeta[optionType];

          const selectedOptionId = newOptions[optionType];

          if (optionId !== selectedOptionId) {
            isSame = false;
            break;
          }

          isSame = true;
        }

        if (isSame) {
          newVariantId = variantId;
          break;
        }
      }
    }

    if (!newVariantId) {
      // checking for next available option.
      // handles the case where variant is not available in available or unavailable list for new options
      newVariantId = handleDeletedVariantOptionsForNewSelection({
        availableVariants,
        optionType,
        newOptionId,
        allOptions
      });
    }

    if (!newVariantId) {
      const errorMessage = `Could not compute new variant id: ${newVariantId}`;
      logException(errorMessage, {
        newOptionId,
        optionType,
        productId,
        unavailableDueTo,
        newVariantId
      });
      redirectToErrorPage(errorMessage);
      return Promise.resolve();
    }

    return {
      newVariantId,
      getNewVariantsMetaPromise: apiGetSingleVariantMeta({
        companyId,
        variantId: newVariantId
      }).then((variantMeta) => {
        mergeNewVariantWithAmalgam(variantMeta);
        if (beforeRedirection) {
          beforeRedirection();
        }

        return Promise.resolve({
          variantId: newVariantId,
          redirect: (variantId) => {
            let startUrl = '/s';
            let slugUrl = getShowcaseSlug();
            let returnUrlStr = '';

            if (getReturnUrl()) {
              const { companySlug } = getRouteParams();
              returnUrlStr = `?${RETURN_URI}=${encodeReturnUriForSearch(getReturnUrl())}`;
              slugUrl = companySlug;
            }

            if (isResellerBundleView(fromBundle)) {
              startUrl = '/t';
            }

            route(`${startUrl}/gallery/${slugUrl}/product/${variantId}${returnUrlStr}`, true);
          }
        });
      })
    };
  } catch (error) {
    logException('Error in selectNewOption', {
      error,
      newOptionId,
      optionType,
      productId,
      unavailableDueTo
    });
    redirectToErrorPage();
  }
};

export const getCustomFields = ({ productId }) => getProduct(productId).customFields || null;

export const getVariantsFromOptions = ({ staticOption = {}, variableOptions, productId }) => {
  const product = getProduct(productId);
  const { parentProductId } = product;

  const parentProduct = getProduct(parentProductId);
  const { availableVariants, unavailableVariants } = parentProduct;
  const allVariants = [...availableVariants, ...unavailableVariants];

  const inStockVariants = [];
  const outOfStockVariants = [];

  variableOptions.forEach((option) => {
    const meta = { optionLabel: option.value };
    const allOptions = [...staticOption.id, option.id].filter((id) => id);
    for (let i = 0; i < allVariants.length; i += 1) {
      const id = allVariants[i];
      const variant = getProduct(id);
      if (!variant) {
        continue;
      }
      const { optionsMeta, dateDeleted, parentProductId } = variant;
      if (!dateDeleted && optionsMeta) {
        const variantOptionIds = getObjectValues(optionsMeta).map(({ optionId }) => optionId);
        const missingOptions = allOptions.filter(
          (optionId) => variantOptionIds.indexOf(optionId) < 0
        );

        if (!missingOptions.length) {
          const parentProduct = getProduct(parentProductId);
          meta.variantId = id;
          meta.price =
            variant.getPriceString() ||
            (parentProduct ? parentProduct.getPriceString() : undefined);
          meta.isOutOfStock = getMaximumItemCount(id) <= 0;
          break;
        }
      }
    }

    if (meta.variantId && !meta.isOutOfStock) {
      inStockVariants.push(meta);
    } else if (meta.variantId && meta.isOutOfStock) {
      outOfStockVariants.push(meta);
    }
  });

  return [...inStockVariants, ...outOfStockVariants];
};

export const isProductSet = ({ productId, product, fromCustomPage }) => {
  if (!product) {
    product = getProduct(productId, fromCustomPage);
  }
  if (!product) {
    return false;
  }

  return product.isSet;
};

export const isVariantSet = ({ productId, product, fromCustomPage }) => {
  if (!product) {
    product = getProduct(productId, fromCustomPage);
  }
  if (!product) {
    return false;
  }

  // Indicates that this is not a variant but rather a product
  if (!product.parentProductId) {
    return false;
  }

  let { optionsMeta, optionMeta } = product;
  if (!optionsMeta) {
    optionsMeta = {};
  }
  if (Object.keys(optionsMeta).length) {
    for (const option of Object.keys(optionsMeta)) {
      const { isSet } = optionsMeta[option];
      if (isSet) {
        return true;
      }
    }
  }

  if (Array.isArray(optionMeta)) {
    for (const option of optionMeta) {
      const { isSet } = option;
      if (isSet) {
        return true;
      }
    }
  }

  return false;
};

export const getVariantSetQuantity = ({ productId, product, fromCustomPage }) => {
  if (!isVariantSet({ productId, product, fromCustomPage })) {
    return null;
  }
  if (!product) {
    product = getProduct(productId, fromCustomPage);
  }

  let { optionsMeta, optionMeta } = product;
  if (!optionsMeta) {
    optionsMeta = {};
  }
  if (Object.keys(optionsMeta).length) {
    let finalSetQunatity = 1;
    Object.keys(optionsMeta).forEach((option) => {
      const { isSet } = optionsMeta[option];
      if (isSet) {
        finalSetQunatity *= optionsMeta[option].setQuantity || 1;
      }
    });
    return finalSetQunatity;
  }

  if (Array.isArray(optionMeta)) {
    let finalCount = 1;
    for (const option of optionMeta) {
      const { setQuantity, isSet } = option;
      let finalSetQunatity = 1;
      if (isSet) {
        finalSetQunatity = setQuantity || 1;
      }
      finalCount *= finalSetQunatity;
    }
    return finalCount;
  }

  return 1;
};

/**
 * This method will determine whether the current product is a variant or not
 * and accordingly will retrieve the set quantity if the product or variant is
 * a set. If it is not a set, then this method will return 1.
 * @param {object} param0 {productId}
 *   productId is the id of the product
 */
export const getProductSetQuantityMultiplier = ({ productId, product, fromCustomPage }) => {
  if (isVariantSet({ productId, product, fromCustomPage })) {
    return getVariantSetQuantity({ productId, product, fromCustomPage }) || 1;
  }

  if (isProductSet({ productId, product, fromCustomPage })) {
    if (!product) {
      return getProduct(productId, fromCustomPage).setQuantity || 1;
    }
    return product.setQuantity || 1;
  }

  return 1;
};

export const getProductPriceAsPerPieceIfSet = ({ product }) => {
  if (!product) {
    return { priceString: '', cutPriceString: '' };
  }

  const priceString = product.getPriceString();
  const cutPriceString = product.getCutPriceString();

  // The current product is not a set or it doesn't have price set, return the existing product values
  if (!(isProductSet({ product }) || isVariantSet({ product })) || !priceString) {
    return { priceString, cutPriceString };
  }

  if (product.setType) {
    const companyUnitDetail = getCompanyUnitDetail(product.setType) || {};
    return {
      priceString: `${priceString} ${
        companyUnitDetail.setType
          ? `${I18n.t('PER')} ${companyUnitDetail.setType}`
          : I18n.t('PER_PIECE')
      }`,
      cutPriceString
    };
  }

  return { priceString: `${priceString} ${I18n.t('PER_PIECE')}`, cutPriceString };
};

export const getProductPictureUrlsAsArray = ({ productId }) => {
  const product = getProduct(productId);
  if (!product) {
    return [];
  }

  const { pictures, default_picture_id: defaultPictureId } = product;
  if (!pictures) {
    return [];
  }

  const pictureArray = [];
  const defaultPicture = pictures[defaultPictureId];
  if (defaultPicture) {
    pictureArray.push(defaultPicture.url);
  }

  const picturesUrl = Object.keys(pictures).reduce((cumulativeArray, pictureId) => {
    if (pictureId !== defaultPictureId) {
      const picture = product.pictures[pictureId];
      cumulativeArray.push(picture.url);
    }
    return cumulativeArray;
  }, pictureArray);

  return picturesUrl;
};

export const getFinalProductSellingPrice = ({ productId }, { includeMargins = false } = {}) => {
  const product = getProduct(productId);
  if (!product) {
    return 0;
  }

  let finalPrice = null;
  if (typeof product.price === 'number') {
    finalPrice = product.price;
  }

  if (typeof product.discounted_price === 'number') {
    finalPrice = product.discounted_price;
  }

  if (typeof finalPrice === 'number' && includeMargins && typeof product.marginPrice === 'number') {
    finalPrice += product.marginPrice;
  }

  return finalPrice;
};

export const getProductVideos = ({ productId }) => {
  const product = getProduct(productId);
  if (!product) {
    return [];
  }

  const productVideos = product.videos || {};

  const videoIds = Object.keys(productVideos);

  return videoIds.reduce((cumulativeArray, videoId) => {
    const { urls, posterUrls, url, prepared, processed, height, width } = productVideos[videoId];

    if (Array.isArray(urls) && prepared && processed) {
      cumulativeArray.push({
        videoId,
        urls,
        url,
        posterUrl: !!posterUrls && !!posterUrls[0] ? posterUrls[0] : '',
        posterUrls,
        height,
        width,
        isVideo: true
      });
    }

    return cumulativeArray;
  }, []);
};

export const getProductDefaultPictureIfVideoSelected = ({ mediaId, mediaType, productId }) => {
  if (mediaType !== MEDIA_TYPES.VIDEO) {
    return mediaId;
  }

  const product = getProduct(productId);
  if (!product) {
    return '';
  }

  return getProductDefaultPictureId(product);
};

export const getProductSchemaProps = ({ productId, fromCustomPage }) => {
  if (!productId) {
    return { productSchemaEnabled: false };
  }
  const product = getProduct(productId, fromCustomPage);
  const currencyOptions = getCurrencyOptions();
  if (
    !product ||
    !product.id ||
    !product.pictures ||
    Object.keys(product.pictures).length === 0 ||
    (!isStringValidForSeoTag(product.name) && !isStringValidForSeoTag(product.sku)) ||
    typeof product.price !== 'number' ||
    !currencyOptions ||
    !currencyOptions.iso2417CurrencyCode
  ) {
    return { productSchemaEnabled: false };
  }
  return { productSchemaEnabled: true, itemType: 'https://schema.org/Product', itemScope: true };
};

export const getProductAccessibilityProps = ({ product }) => {
  if (product && (!!product.name || !!product.sku)) {
    return {
      role: 'img',
      ariaLabel: product.name || product.sku,
      title: product.name || product.sku
    };
  }
  return {};
};

export const getAllProductMedia = ({ productId, shiftVideoToPositionN = -1 }) => {
  const pictures = getPictures({ productId });
  const videos = getProductVideos({ productId });
  const productMediaArray = [...pictures, ...videos];

  if (shiftVideoToPositionN > -1 && videos.length > 0) {
    return shiftVideoPositionWithinProductMediaArray({ productMediaArray, shiftVideoToPositionN });
  }
  return productMediaArray;
};

const shiftVideoPositionWithinProductMediaArray = ({
  productMediaArray = [],
  shiftVideoToPositionN = -1
}) => {
  if (shiftVideoToPositionN > -1 && productMediaArray.length > 1) {
    const firstProductMediaVideo = productMediaArray.find((productMedia) => productMedia.isVideo);
    if (firstProductMediaVideo) {
      productMediaArray.splice(productMediaArray.length - 1, 1);
      productMediaArray.splice(shiftVideoToPositionN, 0, firstProductMediaVideo);
      return productMediaArray;
    }
  }
  return productMediaArray;
};

export const hasSamePictures = ({ currentProductId, prevProductId }) => {
  const { default_picture_id: defaultPictureId, pictures } = getProduct(currentProductId) || {};
  const { default_picture_id: prevDefaultPictureId, pictures: prevPictures } =
    getProduct(prevProductId) || {};

  if (!defaultPictureId || !prevDefaultPictureId) {
    return false;
  }

  const pictureIds = Object.keys(pictures || {});
  const prevPictureIds = Object.keys(prevPictures || {});

  return (
    defaultPictureId === prevDefaultPictureId &&
    pictureIds.length === prevPictureIds.length &&
    !pictureIds.some((pictureId) => !prevPictures[pictureId])
  );
};
